import { getItem, setItem } from '@alltrails/local-storage/utils';
import type { AdvancedMappingState, PersistedAdvancedMapping } from '@/utils/slices/advancedMappingSlice';
import { isToggledMapContentEntry, mapContentEntriesById, type MapContentId } from './mapContent/mapContentRegistry';
import type { MapContentVisibility } from './mapContent/mapContentVisibility';
import { isPoiGroupId } from './mapContent/poiGroups';

// Holds the whole advancedMapping slice; the name predates that and stays so existing users keep their settings
export const advancedMappingStorageKey = 'advanced-mapping-content';

// Bump when the stored shape changes; a mismatch discards the stored value
const storageVersion = 1;

type StoredAdvancedMapping = { v: number } & Partial<PersistedAdvancedMapping>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// The reducer ignores style-backed ids, so a stored one could never be cleared
const isStorableContentId = (id: string) =>
  Object.hasOwn(mapContentEntriesById, id) && !isToggledMapContentEntry(mapContentEntriesById[id as MapContentId]);

const parseStoredVisibility = (contentVisibility: unknown): MapContentVisibility | undefined => {
  if (!isRecord(contentVisibility)) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(contentVisibility).filter(([id, isVisible]) => isStorableContentId(id) && typeof isVisible === 'boolean'));
};

const parseStoredExcludedPoiGroups = (excludedPoiGroups: unknown) =>
  Array.isArray(excludedPoiGroups) ? excludedPoiGroups.filter(isPoiGroupId) : undefined;

export const readStoredAdvancedMapping = (): Partial<PersistedAdvancedMapping> | undefined => {
  const stored = getItem<StoredAdvancedMapping>(advancedMappingStorageKey);
  if (!stored || stored.v !== storageVersion) {
    return undefined;
  }

  const contentVisibility = parseStoredVisibility(stored.contentVisibility);
  const excludedPoiGroups = parseStoredExcludedPoiGroups(stored.excludedPoiGroups);
  const restored = {
    ...(contentVisibility ? { contentVisibility } : {}),
    ...(excludedPoiGroups ? { excludedPoiGroups } : {}),
    // Only off is ever stored, so anything else is left to the default
    ...(stored.showGradeShading === false ? { showGradeShading: false } : {})
  };

  return Object.keys(restored).length > 0 ? restored : undefined;
};

// Named rather than spread, so a field added to the slice stays out of storage until someone decides it belongs there
export const writeStoredAdvancedMapping = ({ contentVisibility, excludedPoiGroups, showGradeShading }: AdvancedMappingState) =>
  setItem<StoredAdvancedMapping>(advancedMappingStorageKey, { contentVisibility, excludedPoiGroups, showGradeShading, v: storageVersion });
